/**
 * Paired current/proposed bar cell for the What-If before/after grid
 * (InputDeltaGrid). Fork lineage: AgenticV2/cards/results/driver-analysis/
 * RightOnlyBarCell, reshaped from one signed bar to a before/after pair.
 *
 * Encoding: the two bars show MAGNITUDE (|value| against the column-wide
 * shared maxAbs, so lengths are comparable across every row and both
 * series). Identity is same-hue sequential — the same measure at two
 * moments, not two categories: Current is the lighter step (#5598E7),
 * Proposed the darker (#1C5CAB). Pair validated (dataviz six-checks,
 * 2026-08-22): all pass; the light step's sub-3:1 surface contrast is
 * relieved by the adjacent formatted value columns (table view), per the
 * validator's own relief rule.
 *
 * A negative or non-numeric value renders NO bar — the formatted value
 * column carries the sign/text. Assumption values on this surface are
 * magnitudes (units, rates, shares); a signed-bar treatment (mirrored
 * axis) is a different form and deliberately out of scope until a real
 * negative-valued input shows up.
 */
import React from 'react';
import type { ICellRendererParams } from 'ag-grid-community';

export const CURRENT_BAR_COLOR = '#5598E7';
export const PROPOSED_BAR_COLOR = '#1C5CAB';

type Params = ICellRendererParams & {
  maxAbs?: number;
};

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

const barWidthPct = (value: unknown, maxAbs: number): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return clamp01(value / maxAbs) * 100;
};

export default function PairedBarCell(params: Params) {
  const maxAbs = Number(params.maxAbs) || 1;
  const before = barWidthPct(params.data?.before, maxAbs);
  const after = barWidthPct(params.data?.after, maxAbs);

  if (before === null && after === null) {
    return null;
  }

  const bar = (pct: number | null, color: string) => (
    <div
      style={{
        // 9px bars + 2px spacer gap (mark-spec spacers): the pair reads
        // as one 20px block, vertically centered in the 40px row.
        height: 9,
        width: `${pct ?? 0}%`,
        background: pct === null ? 'transparent' : color,
        borderRadius: '0 4px 4px 0',
        transition: 'width 250ms ease',
      }}
    />
  );

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        justifyContent: 'center',
      }}
    >
      {bar(before, CURRENT_BAR_COLOR)}
      {bar(after, PROPOSED_BAR_COLOR)}
    </div>
  );
}
